/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AssetType,
  IMediaCategoryModel,
  IPersonModel,
  useMediaGenreList,
  useMediaList,
  useSearchMedia,
  useSearchPeople,
} from "@nf/common";
import {
  DEFAULT_LANGUAGE_CODE,
  SEARCH_MAX_RESULTS,
  SEARCH_QUERY_MIN_LENGTH,
  SEARCH_SORT,
} from "@nf/constants";
import { useState } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";

import { useRouter } from "hooks";
import { useIsTabletDown } from "hooks/useIsTabletDown";
import Button from "components/Button";
import { SearchInput } from "./SearchInput";
import SearchResults from "./SearchResults";
import ChannelsSection from "./ChannelsSection";
import GenresSection from "./GenresSection";

import DicesIcon from "resources/icons/dices.svg";

import styles from "./SearchScreen.module.scss";

const RecommendationsDialog = dynamic(
  () => import("components/RecommendationsDialog"),
  { ssr: false }
);

const SEARCH_FILTERS = {
  PageNumber: 1,
  PageSize: SEARCH_MAX_RESULTS,
};

export interface Channel {
  url: string;
  icon: string;
  ageRestricted: boolean;
}

export type SearchScreenParams = {
  query: string;
  person?: string;
  language?: string;
};

export interface SearchScreenProps {
  enableRecommendations: boolean;
  genreList: IMediaCategoryModel[];
  channels: Channel[];
}

export const SearchScreen = (props: SearchScreenProps): JSX.Element => {
  const { isTabletDown } = useIsTabletDown();
  const { t } = useTranslation("translations");
  const router = useRouter();
  const params = router.query as SearchScreenParams;
  const queryString = params?.query;
  const personString = params?.person;
  const [isRecommendationsDialogOpened, setIsRecommendationsDialogOpened] =
    useState(false);

  const { data: people, loading: isPeopleLoading } = useSearchPeople({
    ...SEARCH_FILTERS,
    FullTextSearch: queryString,
    Sort: "-assetCount",
  });

  const { data: media, loading: isMediaLoading } = useSearchMedia(
    {
      ...SEARCH_FILTERS,
      FullTextSearch: queryString,
      Sort: SEARCH_SORT,
      Locales: params.language || DEFAULT_LANGUAGE_CODE,
      People: people.map((person) => person.PersonId),
    },
    !isPeopleLoading
  );

  const { data: mediaListOfSelectedPerson } = useMediaList({
    ...SEARCH_FILTERS,
    PageSize: 100,
    MediaListId: personString || "",
    QueryParams: {
      assetTypes: [AssetType.Movie, AssetType.Series].join(","),
      participant: personString || "",
      sort: SEARCH_SORT,
    },
  });

  const { data: mediaGenreList } = useMediaGenreList();

  const person = people.find((person) => person.PersonId === personString);

  const onPersonClick = (_index: number, person: IPersonModel) => {
    const query = {
      ...params,
    };

    if (person?.PersonId) {
      query.person = person?.PersonId;
    } else {
      delete query.person;
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const openRecommendationsDialog = () => {
    setIsRecommendationsDialogOpened(true);
  };

  const closeRecommendationsDialog = () => {
    setIsRecommendationsDialogOpened(false);
  };

  return (
    <div className={styles.search}>
      <section>
        <div className={`${styles.pagePadding} ${styles.input}`}>
          {isTabletDown && <SearchInput />}

          {props.enableRecommendations && (
            <Button
              size="medium"
              variant="subtle"
              icon={<DicesIcon />}
              className={styles.recommend}
              onClick={openRecommendationsDialog}
            >
              {t("web_search_choose_button")}
            </Button>
          )}
        </div>

        {queryString && queryString.length >= SEARCH_QUERY_MIN_LENGTH ? (
          <SearchResults
            person={person}
            people={people}
            assets={mediaListOfSelectedPerson || media}
            isLoading={!!isPeopleLoading || !!isMediaLoading}
            onPersonClick={onPersonClick}
          />
        ) : (
          <div className={styles.pagePadding}>
            <ChannelsSection channels={props.channels} />

            <GenresSection genres={props.genreList || mediaGenreList} />
          </div>
        )}

        {props.enableRecommendations && isRecommendationsDialogOpened && (
          <RecommendationsDialog
            visible={isRecommendationsDialogOpened}
            onCancel={closeRecommendationsDialog}
          />
        )}
      </section>
    </div>
  );
};

export default SearchScreen;
